/* eslint-disable max-len, @typescript-eslint/no-unused-expressions */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import withRouter from 'src/hooks/withRouter';
import { confirmWrapper } from 'src/components/Dialog';
import TermsAndConditions from 'src/components/TermsAndConditions';
import FeedbackForm from 'src/components/FeedbackForm';
import { parseUserAgent } from 'src/utils';
import moment from 'moment';
import API from '../../network';
import ViewRenderer from './views';
import {
  AuthorizationFormViewRendererProps,
  FetchAuthorizationFormResponse, SubmitSignatureRequest, RejectSignatureRequest,
} from './types';

function AuthorizationForm() {
  const [loading, setLoading] = useState<boolean>(false);
  const [termsAgreed, setTermsAgreed] = useState(true);
  const [details, setDetails] = useState<FetchAuthorizationFormResponse | undefined >(undefined);
  const { encryptedCandidateId = '' } = useParams();
  const [currentDeviceInfo, setCurrentDeviceInfo] = useState<{
    ipAddress: string; browser: string; time: string,
  } | undefined>();

  const getData = async (): Promise<{
    ipAddress: string; browser: string; time: string,
  } | undefined> => {
    try {
      const respJson = await fetch('https://api64.ipify.org/?format=json');
      const response = await respJson.json();
      const defaultData = {
        ipAddress: response?.ip,
        browser: parseUserAgent(navigator.userAgent as string),
        time: new Date().toUTCString(),
      };

      setCurrentDeviceInfo(defaultData);
      return defaultData;
    } catch (e) {
      setCurrentDeviceInfo(undefined);
      return undefined;
    }
  };

  useEffect(() => {
    const fetchAuthorizationForm = async (): Promise<FetchAuthorizationFormResponse | undefined> => {
      setLoading(true);
      try {
        const response: FetchAuthorizationFormResponse = await API
          .getInstance()
          .getSignatureForm(encryptedCandidateId);
        return response;
      } catch (e) {
        await confirmWrapper('Something went wrong', {
          hideSecondaryBtn: true,
          variant: 'danger',
        });
        return undefined;
      } finally {
        setLoading(false);
      }
    };
    fetchAuthorizationForm().then(async (response) => {
      if (response) {
        setDetails(response);
        if (!response.digitalSignature) {
          const data = await getData();
          setDetails({
            ...response,
            deviceInfo: JSON.stringify(data),
            ipAddress: data?.ipAddress || '',
            updatedDate: moment().format('DD/MM/YYYY HH:mm:ss a'),
          });
        }
      }
    });
  }, []);

  const submitSignature = useCallback(async (signatureData: any, successCallback: () => void, errorCallback: () => void): Promise<void> => {
    try {
      let adaptedSignature = {};
      if (signatureData?.type === 'digital') {
        adaptedSignature = {
          candidateName: signatureData.data.name,
          fontFamily: signatureData.data.fontFamily,
        };
      }
      const request: SubmitSignatureRequest = {
        hashKey: encryptedCandidateId,
        ipAddress: currentDeviceInfo?.ipAddress || '',
        articleTitle: 'AUTHORIZATION_FORM',
        signatureFormURL: '',
        adaptedSignature,
        digitalSignature: signatureData,
        deviceInfo: signatureData.meta,
      };
      setLoading(true);
      await API.getInstance().submitSignature(request);
      successCallback && successCallback();
      await confirmWrapper('Submitted successfully.', {
        variant: 'success',
        autoClose: true,
        primaryBtnLabel: 'ok',
        hideSecondaryBtn: true,
      });
    } catch (e) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
      errorCallback && errorCallback();
    } finally {
      setLoading(false);
    }
  }, [currentDeviceInfo]);

  const onOk = () => {
    setTermsAgreed(true);
  };

  const onOpenTermsAndConditionsPopup = async (): Promise<void> => {
    await confirmWrapper('Terms And Conditions', {
      variant: '',
      hidePrimaryBtn: true,
      hideSecondaryBtn: true,
      maxWidth: 'xs',
      fullWidth: true,
      containerClasses: 'p-4',
      showCloseButton: true,
      disableBackdropClickClose: true,
      disableEscapeKeyDownClose: true,
      primaryBtnLabel: 'I agree',
      dialogContent: (
        <TermsAndConditions
          onOk={(close) => {
            if (close) close();
            onOk();
          }}
        />
      ),
    });
  };

  const onSubmitFeedback = async (feedback: string): Promise<void> => {
    try {
      const request: RejectSignatureRequest = {
        hashKey: encryptedCandidateId,
        feedback,
        ipAddress: currentDeviceInfo?.ipAddress || '',
        articleTitle: 'AUTHORIZATION_FORM',
      };
      await API.getInstance().submitFeedback(request);
      await confirmWrapper('Feedback submitted successfully.', {
        variant: 'success',
        autoClose: true,
        primaryBtnLabel: 'ok',
        hideSecondaryBtn: true,
      });
    } catch (e) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
    }
  };

  const onOpenFeedbackPopup = async (): Promise<void> => {
    await confirmWrapper('Feedback', {
      variant: '',
      hidePrimaryBtn: true,
      hideSecondaryBtn: true,
      maxWidth: 'xs',
      fullWidth: true,
      containerClasses: 'p-4',
      showCloseButton: true,
      disableBackdropClickClose: true,
      disableEscapeKeyDownClose: true,
      dialogContent: (
        <FeedbackForm
          onSubmit={({ feedback }, close) => {
            if (close) close();
            onSubmitFeedback(feedback);
          }}
        />
      ),
    });
  };

  const viewProps: AuthorizationFormViewRendererProps = {
    loading,
    details,
    termsAgreed,
    onOpenTermsAndConditionsPopup,
    onOpenFeedbackPopup,
    submitSignature,
  };

  return <ViewRenderer {...viewProps} />;
}

export default withRouter(AuthorizationForm);
